import React from 'react';
import { Loader, Pagination } from '@mantine/core';
import { APPROVAL_STATUS_LABELS, getApprovalActionLabel } from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import { ApprovalRequestDetail } from './ApprovalRequestDetail';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type Props = Pick<
  UseMonitoringPage,
  | 'approvalRequestRows'
  | 'isApprovalRequestLoading'
  | 'hasLoadedApprovalRequests'
  | 'approvalRequestTotalPages'
  | 'approvalRequestPage'
  | 'setApprovalRequestPage'
  | 'selectedApprovalRequestIndex'
  | 'selectedApprovalRequestDetail'
  | 'isApprovalRequestDetailLoading'
  | 'openApprovalRequestDetail'
>;

const getOrderNumber = (row: Props['approvalRequestRows'][number]) => {
  const orderCode = row.orderCode?.trim();
  if (!orderCode) return String(row.orderId);

  const parts = orderCode.split('/').map((part) => part.trim()).filter(Boolean);
  return parts.length >= 2 ? parts.slice(-2).join('/') : orderCode;
};

const statusClassNames: Record<Props['approvalRequestRows'][number]['status'], string> = {
  approved: classes.approvalStatusApproved,
  denied: classes.approvalStatusDenied,
  pending: classes.approvalStatusPending,
};

export const ApprovalRequestsTable: React.FC<Props> = ({
  approvalRequestRows,
  isApprovalRequestLoading,
  hasLoadedApprovalRequests,
  approvalRequestTotalPages,
  approvalRequestPage,
  setApprovalRequestPage,
  selectedApprovalRequestIndex,
  selectedApprovalRequestDetail,
  isApprovalRequestDetailLoading,
  openApprovalRequestDetail,
}) => {
  if (isApprovalRequestLoading && !hasLoadedApprovalRequests) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  if (selectedApprovalRequestIndex !== null) {
    if (isApprovalRequestDetailLoading || !selectedApprovalRequestDetail) {
      return (
        <div className={classes.loadingWrap}>
          <Loader />
        </div>
      );
    }

    return <ApprovalRequestDetail detail={selectedApprovalRequestDetail} />;
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>{'הפעלה'}</th>
            <th>{'מספר הזמנה'}</th>
            <th>{'סוג פעולה'}</th>
            <th>{'נשלח ע״י'}</th>
            <th>{'אושר/נדחה ע״י'}</th>
            <th>{'סטטוס'}</th>
            <th>{'נוצר ב'}</th>
          </tr>
        </thead>
        <tbody>
          {approvalRequestRows.map((row, index) => (
            <tr key={row.id} className={classes.clickableRow} onClick={() => openApprovalRequestDetail(index)} data-testid={`monitoring-approval-row-${row.id}`}>
              <td data-label={'הפעלה'}>{row.sessionId}</td>
              <td data-label={'מספר הזמנה'}>{getOrderNumber(row)}</td>
              <td data-label={'סוג פעולה'}>{getApprovalActionLabel(row)}</td>
              <td data-label={'נשלח ע״י'}>{row.requestedByName}</td>
              <td data-label={'אושר/נדחה ע״י'}>{row.decidedByName ?? '-'}</td>
              <td data-label={'סטטוס'}>
                <span className={statusClassNames[row.status]}>{APPROVAL_STATUS_LABELS[row.status]}</span>
              </td>
              <td data-label={'נוצר ב'}>{formatDateTime(row.createdAt)}</td>
            </tr>
          ))}
          {approvalRequestRows.length === 0 && (
            <tr>
              <td colSpan={7} className={classes.empty}>
                {'אין בקשות אישור להצגה.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {approvalRequestTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={approvalRequestTotalPages} value={approvalRequestPage} onChange={setApprovalRequestPage} size="sm" />
        </div>
      )}
    </div>
  );
};
